import localApiAdapter from '../../core/services/localApiAdapter';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import i18n from '../../i18n';
import {
  buildGenericSidePanels,
  loadGenericModuleData,
  MODULE_BUILDER_CONFIGS,
} from './builders/genericModuleBuilder';
import { emptyModulePanels } from './agentPanelTypes';

const RECENT_DOCUMENT_LIMIT = 8;
const PAYMENT_ENTRY_LIMIT = 12;

const p = (key, opts) => i18n.t(`modules.shared.aiAgent.sidePanels.${key}`, opts);

function parseAmount(value) {
  const amount = parseFloat(value);
  return Number.isFinite(amount) ? amount : 0;
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getDocumentSortDate(doc) {
  return (
    parseDate(doc.updated_at || doc.updatedAt) ||
    parseDate(doc.date) ||
    parseDate(doc.created_at || doc.createdAt) ||
    new Date(0)
  );
}

function resolveDocTypeLabel(type) {
  const key = String(type || '').toLowerCase();
  if (key === 'invoice' || key === 'facture') return p('doc_type_invoice');
  if (key === 'quote' || key === 'devis') return p('doc_type_quote');
  if (key === 'delivery' || key === 'bon' || key === 'delivery-note') return p('doc_type_delivery');
  if (key === 'purchase-order' || key === 'commande') return p('doc_type_purchase_order');
  if (key === 'payment-schedule') return p('doc_type_payment_schedule');
  if (key === 'credit-note' || key === 'avoir') return p('doc_type_credit_note');
  return p('doc_type_document');
}

function resolveDocStatus(doc) {
  const status = String(doc.status || '').toLowerCase();
  const paymentStatus = String(doc.paymentStatus || doc.payment_status || '').toLowerCase();

  if (status === 'draft' || status === 'brouillon') {
    return { label: p('status_draft'), tone: 'amber' };
  }
  if (paymentStatus === 'paid' || status === 'paid' || status === 'payé' || status === 'paye') {
    return { label: p('status_paid'), tone: 'green' };
  }
  if (paymentStatus === 'partial' || status === 'partial') {
    return { label: p('status_partial'), tone: 'blue' };
  }
  if (paymentStatus === 'overdue' || status === 'overdue') {
    return { label: p('status_overdue'), tone: 'red' };
  }
  if (paymentStatus === 'unpaid' || paymentStatus === 'pending' || status === 'pending') {
    return { label: p('status_pending'), tone: 'slate' };
  }
  if (status) return { label: p('status_other'), tone: 'slate' };
  return { label: '—', tone: 'slate' };
}

function formatShortDate(value) {
  const date = parseDate(value);
  if (!date) return '—';
  return date.toLocaleDateString(getBrandLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function isUnpaidInvoice(doc) {
  const type = String(doc.type || '').toLowerCase();
  if (type !== 'invoice' && type !== 'facture') return false;

  const paymentStatus = String(doc.paymentStatus || doc.payment_status || '').toLowerCase();
  if (['paid'].includes(paymentStatus)) return false;

  const status = String(doc.status || '').toLowerCase();
  if (status === 'draft' || status === 'brouillon' || status === 'paid') return false;

  return (
    ['unpaid', 'partial', 'pending', 'overdue'].includes(paymentStatus) ||
    status === 'pending' ||
    status === 'overdue'
  );
}

function startOfDay(date) {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function endOfWeek(date) {
  const copy = startOfDay(date);
  const day = copy.getDay();
  const diff = day === 0 ? 0 : 7 - day;
  copy.setDate(copy.getDate() + diff);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

function buildRecentDocuments(documents = []) {
  return [...documents]
    .sort((a, b) => getDocumentSortDate(b) - getDocumentSortDate(a))
    .slice(0, RECENT_DOCUMENT_LIMIT)
    .map((doc) => {
      const number = doc.number || doc.reference || `#${doc.id}`;
      const typeLabel = resolveDocTypeLabel(doc.type);
      const client = doc.clientName || doc.client_name || doc.client || p('unknown_client');
      const amountValue = parseAmount(doc.amount || doc.total || doc.total_ttc);
      const status = resolveDocStatus(doc);
      const amount = formatBrandMoney(amountValue);

      return {
        id: String(doc.id),
        number,
        typeLabel,
        client,
        amount,
        amountValue,
        statusLabel: status.label,
        statusTone: status.tone,
        dateLabel: formatShortDate(doc.date || doc.updated_at || doc.created_at),
        prompt: p('prompt_document_details', {
          number,
          typeLabel,
          client,
          amount,
          statusLabel: status.label,
        }),
      };
    });
}

function pushPaymentEntry(entries, entry) {
  entries.push(entry);
}

function classifyDueTiming(dueDate, today, weekEnd) {
  const due = startOfDay(dueDate);
  if (due < today) return 'overdue';
  if (due <= weekEnd) return 'week';
  return 'upcoming';
}

function buildPaymentEntries(documents = []) {
  const entries = [];
  const today = startOfDay(new Date());
  const weekEnd = endOfWeek(today);

  documents.forEach((doc) => {
    if (!isUnpaidInvoice(doc)) return;

    const client = doc.clientName || doc.client_name || doc.client || p('unknown_client');
    const invoiceNumber = doc.number || doc.reference || `#${doc.id}`;
    const installments = Array.isArray(doc.installments) ? doc.installments : [];

    if (installments.length > 0) {
      installments.forEach((installment, index) => {
        const instStatus = String(installment.status || 'pending').toLowerCase();
        if (instStatus === 'paid' || instStatus === 'payé' || instStatus === 'paye') return;

        const dueDate = parseDate(installment.dueDate || installment.due_date);
        if (!dueDate) return;

        const amountValue = parseAmount(installment.amount);
        const timing = classifyDueTiming(dueDate, today, weekEnd);
        const amount = formatBrandMoney(amountValue);
        const dueDateLabel = formatShortDate(dueDate);

        pushPaymentEntry(entries, {
          id: `${doc.id}-inst-${index}`,
          timing,
          dueDate,
          title: p('installment_title', { index: index + 1, invoiceNumber }),
          subtitle: `${client} • ${amount} • ${dueDateLabel}`,
          amountValue,
          client,
          invoiceNumber,
          prompt: p('prompt_installment_followup', {
            index: index + 1,
            invoiceNumber,
            client,
            amount,
            dueDate: dueDateLabel,
          }),
        });
      });
      return;
    }

    const dueDate = parseDate(doc.dueDate || doc.due_date || doc.date);
    if (!dueDate) return;

    const amountValue = parseAmount(doc.amount || doc.total || doc.total_ttc);
    const paymentStatus = String(doc.paymentStatus || doc.payment_status || '').toLowerCase();
    const timing = classifyDueTiming(dueDate, today, weekEnd);
    const amount = formatBrandMoney(amountValue);
    const dueDateLabel = formatShortDate(dueDate);

    pushPaymentEntry(entries, {
      id: `${doc.id}-due`,
      timing,
      dueDate,
      title:
        paymentStatus === 'partial'
          ? p('partial_payment_title', { invoiceNumber })
          : p('due_title', { invoiceNumber }),
      subtitle: `${client} • ${amount} • ${dueDateLabel}`,
      amountValue,
      client,
      invoiceNumber,
      prompt: p('prompt_payment_analysis', {
        invoiceNumber,
        client,
        amount,
        dueDate: dueDateLabel,
      }),
    });
  });

  const priority = { overdue: 0, week: 1, upcoming: 2 };

  return entries
    .sort((a, b) => {
      const timingDiff = (priority[a.timing] ?? 9) - (priority[b.timing] ?? 9);
      if (timingDiff !== 0) return timingDiff;
      return a.dueDate - b.dueDate;
    })
    .slice(0, PAYMENT_ENTRY_LIMIT)
    .map(({ dueDate, ...entry }) => entry);
}

function groupPaymentEntries(entries = []) {
  const groups = [
    { id: 'overdue', label: p('group_overdue'), items: [] },
    { id: 'week', label: p('group_this_week'), items: [] },
    { id: 'upcoming', label: p('group_upcoming'), items: [] },
  ];

  entries.forEach((entry) => {
    const group = groups.find((item) => item.id === entry.timing) || groups[2];
    group.items.push(entry);
  });

  return groups.filter((group) => group.items.length > 0);
}

async function loadFacturationPanelData() {
  const documents = await localApiAdapter.documents?.getDocuments?.().catch(() => []);
  const list = Array.isArray(documents) ? documents : [];

  const paymentEntries = buildPaymentEntries(list);
  const recentDocuments = buildRecentDocuments(list);
  const paymentGroups = groupPaymentEntries(paymentEntries);

  return {
    panels: {
      documents: {
        type: 'documents',
        title: p('panel_recent_documents'),
        items: recentDocuments,
        count: recentDocuments.length,
        emptyMessage: p('empty_documents'),
      },
      payments: {
        type: 'payments',
        title: p('panel_payments'),
        groups: paymentGroups,
        count: paymentEntries.length,
        emptyMessage: p('empty_payments'),
      },
    },
    counts: {
      documentsCount: recentDocuments.length,
      paymentsCount: paymentEntries.length,
    },
    recentDocuments,
    paymentGroups,
    paymentCount: paymentEntries.length,
  };
}

async function loadGenericPanelData(moduleId) {
  const loaded = await loadGenericModuleData(moduleId);
  const { panels, counts } = buildGenericSidePanels({ ...loaded, moduleId });

  const docPanel = panels.documents;
  const recentDocuments = (docPanel?.items || []).map((item) => ({
    id: item.id,
    number: item.title,
    typeLabel: p('doc_type_document'),
    client: item.subtitle?.split(' • ')[0] || '—',
    amount: '—',
    statusLabel: item.statusLabel,
    statusTone: item.statusTone,
    dateLabel: item.meta?.date || '—',
    prompt: item.prompt,
  }));

  return {
    panels,
    counts,
    recentDocuments,
    paymentGroups: [],
    paymentCount: 0,
  };
}

const MODULE_PANEL_BUILDERS = {
  facturation: loadFacturationPanelData,
};

Object.keys(MODULE_BUILDER_CONFIGS).forEach((moduleId) => {
  MODULE_PANEL_BUILDERS[moduleId] = () => loadGenericPanelData(moduleId);
});

export async function buildAgentSidePanels(moduleId) {
  const builder = MODULE_PANEL_BUILDERS[moduleId];
  if (!builder) return emptyModulePanels();

  try {
    return await builder();
  } catch (error) {
    console.error('AI Agent: failed to build side panels', error);
    return emptyModulePanels();
  }
}
